import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, LogOut, Menu } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import MobileBottomNav from '../components/MobileBottomNav';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import SeoMainImage from '../components/SeoMainImage';
import HreflangTags from '../components/HreflangTags';
import ToolSidebar from '../components/ToolSidebar';
import TeamProfileModal from '../components/TeamProfileModal';
import axios from 'axios';
import '../styles/TeamPage.css';
import '../styles/HomePage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";

const TeamPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const seo = useSEO('team', {
    title: 'Our Team | Visa Consultants & Travel Experts in Dubai — Registan Tourism',
    description: 'Meet the Registan Tourism team — experienced visa consultants, immigration advisors, and travel specialists based in Dubai.',
    keywords: 'registan tourism team, dubai visa consultant, UAE immigration advisor, travel expert dubai'
  });
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/team`);
        if (response.data && response.data.length > 0) {
          setTeam(response.data);
        }
      } catch (error) {
        console.error('Error fetching team:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  return (
    <div className="team-page">
      <Helmet>
        <link rel="canonical" href="https://registantours.com/team" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/team" />
        <meta property="og:site_name" content="Registan Tourism" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Header */}
      <header className="dark-header">
        <div className="header-left">
          <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={currentLogo} alt="Registan Tourism" className="logo-img" />
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        <nav className="dark-nav desktop-hide-nav">
          <Link to="/" className="dark-nav-link">{t('home') || 'Home'}</Link>
          <Link to="/tours" className="dark-nav-link">{t('teamPageNavTours') || 'Tours'}</Link>
          <Link to="/team" className="dark-nav-link active">{t('team') || 'Team'}</Link>
          <Link to="/start-business" className="dark-nav-link">{t('startBusiness') || 'Start Business'}</Link>
          <Link to="/about" className="dark-nav-link">{t('about') || 'About'}</Link>
        </nav>
        {isAuthenticated ? (
          <div className="header-user-menu">
            <button className="btn-user-profile" onClick={() => navigate('/dashboard')}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => navigate('/login')}>{t('login') || 'Login'}</button>
            <button className="btn-primary btn-signup" onClick={() => navigate('/register')}>{t('signUp') || 'SIGN UP'}</button>
          </div>
        )}
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>
      <SeoMainImage seo={seo} />
      <HreflangTags path="/team" />

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      <div className="tool-page-layout">
        <ToolSidebar />
        <div className="tool-page-content">

      {/* Hero Section */}
      <section className="team-hero">
        <div className="team-hero-content">
          <h2>{t('teamPageHeroTitle') || 'Meet the Experts Behind Registan Tourism'}</h2>
          <p>{t('teamPageHeroDesc') || 'Our dedicated team of visa specialists is here to make your travel dreams a reality. With years of experience and a commitment to excellence, we ensure a seamless visa process for you.'}</p>
        </div>
      </section>

      {/* Team Grid */}
      <section className="team-grid-section">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{t('teamPageLoading') || 'Loading team...'}</p>
          </div>
        ) : (
          <div className="team-grid">
            {team.map((member) => (
              <div 
                key={member.id} 
                className="team-member-card"
                onClick={() => setSelectedMember(member)}
                data-testid={`team-member-${member.id}`}
              >
                {member.image ? (
                  <img src={member.image} alt={member.name} className="member-avatar-img" />
                ) : (
                  <div className="member-avatar">
                    {member.name?.charAt(0)}
                  </div>
                )}
                <div className="member-info">
                  <h3>{member.name}</h3>
                  {(() => {
                    // Prefer admin-managed RU/UZ values when current language matches
                    const localizedTitle = (language === 'ru' && member.position_title_ru) ? member.position_title_ru
                      : (language === 'uz' && member.position_title_uz) ? member.position_title_uz
                      : (member.position_title || member.role);
                    return localizedTitle ? (
                      <span className="member-role">{localizedTitle}</span>
                    ) : null;
                  })()}
                </div>
                {(() => {
                  const localizedBio = (language === 'ru' && member.description_ru) ? member.description_ru
                    : (language === 'uz' && member.description_uz) ? member.description_uz
                    : (member.description || member.bio);
                  return localizedBio ? (
                    <p className="member-bio">{localizedBio}</p>
                  ) : null;
                })()}
                <div className="member-contact">
                  {member.email && (
                    <div className="contact-row">
                      <Mail size={16} />
                      <span>{member.email}</span>
                    </div>
                  )}
                  {(member.mobile || member.phone) && (
                    <div className="contact-row">
                      <Phone size={16} />
                      <span>{member.mobile || member.phone}</span>
                    </div>
                  )}
                  {member.location && (
                    <div className="contact-row">
                      <MapPin size={16} />
                      <span>{member.location}</span>
                    </div>
                  )}
                </div>
                <button className="btn-view-profile">{t('teamPageViewProfile') || 'View Profile'}</button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Team Profile Modal */}
      {selectedMember && (
        <TeamProfileModal
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}

      <SiteFooter />
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default TeamPage;
